import React, { useContext } from 'react';
import { Autosuggest } from '@amzn/awsui-components-react/polaris';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';

/** Max suggestions rendered at once — the LOVs (companies, GL accounts) are large. */
const MAX_SUGGESTIONS = 50;

/**
 * Single-value COA autosuggest backed by the CLI data-source LOV (`DataSourcesService.getCoaData`).
 * Reused for the GL-pull column's `entity` (company) and `account` (GL account) fields — the two
 * segment `pullCriteria` keys that map cleanly from the existing CLI pickers. `segmentKey` is a
 * `COA_SEGMENT_MAPPING.*.WEB_API` value (e.g. 'companies', 'glAccounts').
 *
 * These LOVs can be very large, so suggestions are shown only once the user starts typing (manual
 * filtering, capped) — clicking the empty field does not dump the full list, which would slow the UI.
 *
 * `services` is read from `SegmentTablesContext` rather than threaded as a prop, matching how the rest
 * of the wizard consumes the collection.
 */
export default function SegmentCoaAutosuggest(props: {
    segmentKey: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}) {
    const { segmentKey, value, onChange, placeholder, disabled } = props;
    const { services } = useContext(SegmentTablesContext);
    const [options, loading] = services.dataSourcesService.getCoaData(segmentKey);

    const query = (value ?? '').trim().toLowerCase();
    const suggestions =
        query.length === 0
            ? []
            : (options ?? [])
                  .filter((o) => o.toLowerCase().includes(query))
                  .slice(0, MAX_SUGGESTIONS)
                  .map((o) => ({ value: o }));

    return (
        <Autosuggest
            value={value ?? ''}
            disabled={disabled}
            onChange={({ detail }) => onChange(detail.value)}
            options={suggestions}
            filteringType="manual"
            statusType={loading ? 'loading' : 'finished'}
            loadingText="Loading values"
            placeholder={placeholder}
            enteredTextLabel={(v) => `Use "${v}"`}
            empty={query.length === 0 ? 'Type to search' : 'No matches found'}
        />
    );
}
